import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import Header from "./components/Header";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Home from "./pages/Home";
import ProductPage from "./pages/ProductPage";
import AddReviewPage from "./pages/AddReviewPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ProfilePage from "./pages/ProfilePage";
import Success from "./pages/Success";
import Tracking from "./pages/Tracking";
import Cancel from "./pages/Cancel";
import AdminReviewsPage from "./pages/AdminReviewsPage";
import ReviewsPage from "./pages/ReviewsPage";
import ApprovedReviews from "./components/ApprovedReviews";
import AdminLogin from "./pages/AdminLogin";
import { Routes, Route, Navigate } from "react-router-dom";
import "./responsive.css";
import { supabase } from "./supabaseClient";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function App() {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [favOpen, setFavOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guestCart, setGuestCart] = useState([]);
  const [guestFavorites, setGuestFavorites] = useState([]);
  const [userData, setUserData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const productos = [
    {
      nombre: "Cepillo dental eléctrico",
      precio: 100,
      imagen: "/images/cepillo.jpg",
      descripcion: "Cepillo dental eléctrico para una limpieza profunda, elimina placa y cuida las encías.",
      modoUso: [
  "Aplica una pequeña cantidad de pasta dental en el cabezal.",
  "Coloca el cepillo en tus dientes y enciéndelo.",
  "Cepilla suavemente en pequeños movimientos durante 2 minutos.",
  "Enjuaga tu boca y limpia el cabezal del cepillo.",
  "Reemplaza el cabezal periódicamente."
],

     beneficios: [
  "Elimina más placa que un cepillo manual.",
  "Protege las encías con tecnología de vibración suave.",
  "Reduce el mal aliento y mejora la salud dental.",
  "Ideal para personas con sensibilidad dental.",
  "Ayuda a obtener una limpieza profesional en casa."
],

    },
    {
      nombre: "Crema hidratante facial ligera",
      precio: 3500,
      imagen: "/images/crema.jpg",
      descripcion: "La crema hidratante facial está formulada para aportar hidratación profunda sin generar sensación grasa. Incluye agentes emolientes y nutrientes que fortalecen la barrera cutánea, proporcionando suavidad inmediata y frescura duradera. Su textura ligera la hace adecuada para todo tipo de piel, incluso piel sensible, ayudando a mantener la piel equilibrada, suave y protegida frente a la deshidratación diaria.",
      modoUso: [
        "Aplica sobre el rostro limpio y seco.",
        "Extiende una pequeña cantidad con movimientos ascendentes.",
        "Úsala por la mañana y noche para mejores resultados.",
      ],
      beneficios: [
        "Hidrata en profundidad sin sensación grasa.",
        "Aporta suavidad y frescura inmediata.",
        "Ideal para todo tipo de piel, incluso las sensibles.",
      ],
    },
    {
      nombre: "Retinol serum antiedad con colágeno",
      precio: 5500,
      imagen: "/images/retinol.jpg",
      descripcion: "Serum antiedad con retinol y colágeno, es un tratamiento antiedad concentrado formulado con retinol y colágeno hidrolizado. Ayuda a disminuir líneas de expresión visibles, mejorar la elasticidad de la piel y estimular la producción natural de colágeno. Su uso continuo favorece una piel firme, uniforme y rejuvenecida. Indicado para rutinas nocturnas, acompañado siempre de protección solar al día siguiente.",
      modoUso: [
        "Aplica 2-3 gotas sobre la piel limpia por la noche.",
        "Evita el contorno de ojos y labios.",
        "Aplica protector solar durante el día siguiente.",
      ],
      beneficios: [
        "Disminuye líneas de expresión y arrugas.",
        "Aumenta la elasticidad de la piel.",
        "Estimula la producción natural de colágeno.",
      ],
    },
  ];

  // 🔹 Registro Supabase
  const handleRegister = async (
    email,
    password,
    nombre,
    telefono,
    direccion,
    pais,
    fecha_nacimiento
  ) => {
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) {
        alert("❌ Error al registrar: " + error.message);
        return;
      }
      const user = data?.user;
      if (user) {
        const { error: profileError } = await supabase.from("user_profiles").insert([
          {
            id: user.id,
            nombre,
            email,
            telefono,
            direccion,
            pais,
            fecha_nacimiento,
            avatar_url: "",
            created_at: new Date(),
          },
        ]);
        if (profileError) {
          console.error("Error creando perfil:", profileError.message);
          alert("❌ Error creando perfil: " + profileError.message);
        } else {
          alert("✅ Registro exitoso. Revisa tu correo para verificar tu cuenta.");
        }
      }
    } catch (err) {
      console.error(err);
      alert("Ocurrió un error inesperado al registrarte.");
    }
  };

  // 🔹 Login y Logout
  const handleLogin = async (email, password) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return alert("❌ Error al iniciar sesión: " + error.message);
      alert("✅ Sesión iniciada correctamente.");
    } catch (err) {
      console.error(err);
      alert("Ocurrió un error inesperado al iniciar sesión.");
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setLoggedInUser(null);
  };

  // 🔹 Detectar sesión activa
  useEffect(() => {
    const initSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.user) setLoggedInUser(data.session.user);
      supabase.auth.onAuthStateChange((_event, session) => {
        setLoggedInUser(session?.user || null);
      });
    };
    initSession();
  }, []);

  // 🔹 LocalStorage
  useEffect(() => {
    setGuestCart(JSON.parse(localStorage.getItem("guestCart")) || []);
    setGuestFavorites(JSON.parse(localStorage.getItem("guestFavorites")) || []);
  }, []);

  useEffect(() => {
    localStorage.setItem("guestCart", JSON.stringify(guestCart));
  }, [guestCart]);

  useEffect(() => {
    localStorage.setItem("guestFavorites", JSON.stringify(guestFavorites));
  }, [guestFavorites]);

  // 🔹 Carrito y favoritos
  const getCart = () =>
    loggedInUser ? userData[loggedInUser.id]?.cart || [] : guestCart;
  const getFavorites = () =>
    loggedInUser ? userData[loggedInUser.id]?.favorites || [] : guestFavorites;

  const addToCart = (item) => {
    if (loggedInUser) {
      const id = loggedInUser.id;
      setUserData((prev) => ({
        ...prev,
        [id]: { ...prev[id], cart: [...(prev[id]?.cart || []), item] },
      }));
    } else {
      setGuestCart((prev) => [...prev, item]);
    }
  };

  const removeFromCart = (nombre) => {
    if (loggedInUser) {
      const id = loggedInUser.id;
      const cart = getCart();
      setUserData((prev) => ({
        ...prev,
        [id]: { ...prev[id], cart: cart.filter((i) => i.nombre !== nombre) },
      }));
    } else {
      setGuestCart((prev) => prev.filter((i) => i.nombre !== nombre));
    }
  };

  const toggleFavorite = (prod) => {
    if (loggedInUser) {
      const id = loggedInUser.id;
      const favs = getFavorites();
      const exists = favs.find((f) => f.nombre === prod.nombre);
      setUserData((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          favorites: exists
            ? favs.filter((f) => f.nombre !== prod.nombre)
            : [...favs, prod],
        },
      }));
    } else {
      const exists = guestFavorites.find((f) => f.nombre === prod.nombre);
      setGuestFavorites(
        exists
          ? guestFavorites.filter((f) => f.nombre !== prod.nombre)
          : [...guestFavorites, prod]
      );
    }
  };

  const removeFavorite = (nombre) => {
    if (loggedInUser) {
      const id = loggedInUser.id;
      const favs = getFavorites();
      setUserData((prev) => ({
        ...prev,
        [id]: { ...prev[id], favorites: favs.filter((f) => f.nombre !== nombre) },
      }));
    } else {
      setGuestFavorites((prev) => prev.filter((f) => f.nombre !== nombre));
    }
  };

  // 💳 Checkout con Stripe + fix “atrás”
  const handleCheckout = async () => {
    const cart = getCart();
    if (!cart.length) return alert("Carrito vacío");

    const items = cart.map((item) => ({ ...item, quantity: 1 }));

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/create-checkout-session`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    items,
    user_id: loggedInUser?.id || null,
    email: loggedInUser?.email || null,
  }),
});



      const data = await res.json();
      if (!data?.id) return alert("Error al crear sesión de pago");

      const stripe = await stripePromise;

      // 🧩 FIX MEJORADO: evita pantalla en blanco al volver desde Stripe
      const currentUrl = window.location.href;
      window.history.pushState({ checkout: true }, "", currentUrl);

      window.addEventListener("popstate", (event) => {
        if (event.state?.checkout) {
          console.log("↩️ Volviendo desde Stripe, recargando app...");
          window.location.href = "/"; // redirige a la home y React recarga
        }
      });

      await stripe.redirectToCheckout({ sessionId: data.id });
    } catch (err) {
      console.error(err);
      alert("Error de conexión con Stripe");
    }
  };

  return (
    <div style={{ fontFamily: "Arial", minHeight: "100vh", background: "#f2f2f2" }}>
      <Header
        loggedInUser={loggedInUser}
        accountOpen={accountOpen}
        setAccountOpen={setAccountOpen}
        favOpen={favOpen}
        setFavOpen={setFavOpen}
        dropdownOpen={dropdownOpen}
        setDropdownOpen={setDropdownOpen}
        getCart={getCart}
        getFavorites={getFavorites}
        handleLogout={handleLogout}
        removeFromCart={removeFromCart}
        removeFavorite={removeFavorite}
        handleCheckout={handleCheckout}
        setUserData={setUserData}
        setGuestCart={setGuestCart}
        toggleFavorite={toggleFavorite}
        onSearch={setSearchTerm}
      />

      <Routes>
<Route path="/admin-login" element={<AdminLogin />} />


{/*para la aprobacion de las reseñas de admin (aprovedreviews.js*/}
<Route path="/reseñas-aprobadas" element={<ApprovedReviews />} />

{ /* reviewpage */}
<Route path="/reviews" element={<ReviewsPage />} />

{/*adminreviewspage - 🔐 RUTA PROTEGIDA */} 
<Route
  path="/admin-reviews"
  element={
    localStorage.getItem("adminToken") ? (
      <AdminReviewsPage />
    ) : (
      <Navigate to="/admin-login" replace />
    )
  }
/>



        <Route
          path="/profile"
          element={
            <ProfilePage
              loggedInUser={loggedInUser}
              handleLogout={handleLogout}
              getFavorites={getFavorites}
            />
          }
        />
        
        <Route path="/login" element={<LoginPage handleLogin={handleLogin} />} />
        <Route
          path="/register"
          element={<RegisterPage handleRegister={handleRegister} />}
        />
        <Route path="/add-review" element={<AddReviewPage />} />

        {/* HOME */}
        <Route
          path="/"
          element={
            <Home
              productos={productos}
              addToCart={addToCart}
              toggleFavorite={toggleFavorite}
              getFavorites={getFavorites}
              searchTerm={searchTerm}
            />
          }
        />

        {/* PRODUCTO */}
        <Route
          path="/product/:nombre"
          element={
            <ProductPage
              productos={productos}
              addToCart={addToCart}
              stripePromise={stripePromise}
            />
          }
        />

        {/* STRIPE */}
        <Route path="/success" element={<Success />} />
    
<Route path="/cancel" element={<Cancel />} />  {/* ✅ nueva ruta */}
<Route path="/tracking" element={<Tracking />} />

        
      </Routes>
    </div>
  );
}

export default App;
